import React, { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Container, IconButton, InputAdornment,
  ListItemText, MenuItem, Paper, TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useNotifier } from "../../Core/Notifier";
import { API_URL } from "../../config";
import VendorCredentialsModal from "./VendorCredentialsModal";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35",
  redTx: "#9b2c2c", redBg: "#fef2f2",
  peach: "#ffebd3", inputBg: "#f8f7f5",
};

const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg, border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold }, transition: "all 0.15s", "& fieldset": { border: "none" } },
  "& .MuiInputLabel-root": { fontSize: 13 }, "& input": { py: "9px" },
};

const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const COUNTRIES = [{ code: "CA", name: "Canada" }, { code: "US", name: "United States" }];

const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const post = (p: string, b: any) => fetch(`${API_URL}${p}`, { method: "POST", headers: hdrs(), body: JSON.stringify(b) }).then(r => r.json());
const get_ = (p: string) => fetch(`${API_URL}${p}`, { headers: hdrs() }).then(r => r.json());

interface VendorForm {
  first_name: string; last_name: string; company_name: string;
  email: string; phone: string; alternate_phone: string; website: string;
  address_line_1: string; address_line_2: string; city: string;
  province: string; postal_code: string; country: string;
  notes: string; status: string;
  service_category_ids: number[];
}

const EMPTY: VendorForm = {
  first_name: "", last_name: "", company_name: "",
  email: "", phone: "", alternate_phone: "", website: "",
  address_line_1: "", address_line_2: "", city: "",
  province: "ON", postal_code: "", country: "CA",
  notes: "", status: "active",
  service_category_ids: [],
};

const VendorCreatePage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [form, setForm] = useState<VendorForm>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [loadingVendor, setLoadingVendor] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof VendorForm, string>>>({});

  // Credentials modal
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [createdVendorId, setCreatedVendorId] = useState<number | null>(null);
  const [createdVendorName, setCreatedVendorName] = useState("");
  const [createdVendorEmail, setCreatedVendorEmail] = useState("");

  // Service tree for category selection
  const [serviceTree, setServiceTree] = useState<any[]>([]);

  useEffect(() => {
    get_("/banquet/service-tree").then(res => {
      if (res.code === 200) setServiceTree(res.data || []);
    });
  }, []);

  // Flatten categories from the service tree: { id, name, serviceName }
  const allCategories = serviceTree.flatMap((svc: any) =>
    (svc.categories || []).map((cat: any) => ({ id: cat.id, name: cat.name, serviceName: svc.name }))
  );

  useEffect(() => {
    if (isEdit && id) {
      setLoadingVendor(true);
      get_(`/vendors/manage/${id}`).then(res => {
        if (res.code === 200 && res.data?.vendor) {
          const v = res.data.vendor;
          setForm({
            first_name: v.first_name || "",
            last_name: v.last_name || "",
            company_name: v.company_name || "",
            email: v.email || "",
            phone: v.phone || "",
            alternate_phone: v.alternate_phone || "",
            website: v.website || "",
            address_line_1: v.address_line_1 || v.street_address || "",
            address_line_2: v.address_line_2 || "",
            city: v.city || "",
            province: v.province || "ON",
            postal_code: v.postal_code || "",
            country: v.country || "CA",
            notes: v.notes || "",
            status: v.status || "active",
            service_category_ids: v.service_category_ids || [],
          });
        }
        setLoadingVendor(false);
      });
    }
  }, [isEdit, id]);

  const set = (f: keyof VendorForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(p => ({ ...p, [f]: e.target.value }));
    if (errors[f]) setErrors(p => ({ ...p, [f]: undefined }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof VendorForm, string>> = {};
    if (!form.first_name.trim()) errs.first_name = "First name is required";
    if (!form.last_name.trim()) errs.last_name = "Last name is required";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Invalid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload: any = { ...form };
    if (isEdit && id) payload.id = id;

    const res = await post("/vendors/manage", payload);
    if (res.code === 200 && res.status) {
      showMessage(res.message);
      if (!isEdit && res.data?.id) {
        // Open credentials modal for new vendor
        setCreatedVendorId(res.data.id);
        setCreatedVendorName(res.data.name || `${form.first_name} ${form.last_name}`);
        setCreatedVendorEmail(form.email);
        setCredModalOpen(true);
      } else {
        navigate("/vendors/list");
      }
    } else {
      showErrorMessage(res.message || "Failed to save vendor");
    }
    setSaving(false);
  };

  const handleCredentialsDone = () => {
    setCredModalOpen(false);
    navigate("/vendors/list");
  };

  if (loadingVendor) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>;
  }

  return (
    <Container maxWidth="md" sx={{ pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, pt: 1 }}>
        <IconButton onClick={() => navigate("/vendors/list")} sx={{ color: T.slateLt }}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: `${T.gold}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.navy }}>{isEdit ? "Edit Vendor" : "New Vendor"}</Typography>
          <Typography sx={{ fontSize: 12, color: T.slateLt }}>{isEdit ? "Update vendor information" : "Fill in the details to register a new vendor"}</Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${T.line}`, overflow: "hidden" }}>
        {/* Personal Information */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt, mb: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Personal Information</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField label="First Name *" value={form.first_name} onChange={set("first_name")} size="small" sx={inputSx} error={!!errors.first_name} helperText={errors.first_name} />
            <TextField label="Last Name *" value={form.last_name} onChange={set("last_name")} size="small" sx={inputSx} error={!!errors.last_name} helperText={errors.last_name} />
          </Box>
        </Box>

        {/* Company Information */}
        <Box sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt, mb: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Company Information</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField label="Company Name" value={form.company_name} onChange={set("company_name")} size="small" sx={inputSx} />
            <TextField label="Website" value={form.website} onChange={set("website")} size="small" sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><LanguageOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
          </Box>
        </Box>

        {/* Service Categories */}
        <Box sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt, mb: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Service Categories</Typography>
          <TextField
            label="Select Categories"
            select
            size="small"
            fullWidth
            sx={inputSx}
            SelectProps={{
              multiple: true,
              value: form.service_category_ids,
              onChange: (e: any) => setForm(p => ({ ...p, service_category_ids: e.target.value as number[] })),
              renderValue: (selected: any) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as number[]).map(id => {
                    const cat = allCategories.find((c: any) => c.id === id);
                    return <Chip key={id} label={cat ? cat.name : `#${id}`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: `${T.gold}15`, color: T.navy }} />;
                  })}
                </Box>
              ),
            }}
          >
            {allCategories.map((cat: any) => (
              <MenuItem key={cat.id} value={cat.id}>
                <Checkbox size="small" checked={form.service_category_ids.includes(cat.id)} sx={{ p: 0.5, mr: 1 }} />
                <ListItemText
                  primary={cat.name}
                  secondary={cat.serviceName}
                  primaryTypographyProps={{ fontSize: 12 }}
                  secondaryTypographyProps={{ fontSize: 10 }}
                />
              </MenuItem>
            ))}
            {allCategories.length === 0 && (
              <MenuItem disabled><Typography sx={{ fontSize: 12, color: T.slateLt }}>No categories available</Typography></MenuItem>
            )}
          </TextField>
          <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.5 }}>Select which service categories this vendor handles</Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt, mb: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Contact Information</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
            <TextField label="Email" value={form.email} onChange={set("email")} size="small" sx={inputSx} error={!!errors.email} helperText={errors.email}
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
            <TextField label="Phone" value={form.phone} onChange={set("phone")} size="small" sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
            <TextField label="Alternate Phone" value={form.alternate_phone} onChange={set("alternate_phone")} size="small" sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
          </Box>
        </Box>

        {/* Address */}
        <Box sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt, mb: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Address</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 2 }}>
            <TextField label="Address Line 1" value={form.address_line_1} onChange={set("address_line_1")} size="small" fullWidth sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
            <TextField label="Address Line 2" value={form.address_line_2} onChange={set("address_line_2")} size="small" fullWidth sx={inputSx} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 100px 100px 100px" }, gap: 2 }}>
            <TextField label="City" value={form.city} onChange={set("city")} size="small" sx={inputSx} />
            <TextField label="Province" value={form.province} onChange={set("province")} size="small" select sx={inputSx}>
              {PROVINCES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField label="Postal Code" value={form.postal_code} onChange={set("postal_code")} size="small" sx={inputSx} />
            <TextField label="Country" value={form.country} onChange={set("country")} size="small" select sx={inputSx}>
              {COUNTRIES.map(c => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
            </TextField>
          </Box>
        </Box>

        {/* Notes & Status */}
        <Box sx={{ px: 3, pt: 1, pb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slateLt, mb: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Additional</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 160px" }, gap: 2 }}>
            <TextField label="Notes" value={form.notes} onChange={set("notes")} size="small" multiline rows={3} sx={inputSx} />
            <TextField label="Status" value={form.status} onChange={set("status")} size="small" select sx={inputSx}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}`, bgcolor: T.bg }}>
          <Button onClick={() => navigate("/vendors/list")} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" }, "&.Mui-disabled": { bgcolor: T.line } }}>
            {saving ? "Saving..." : isEdit ? "Update Vendor" : "Create Vendor"}
          </Button>
        </Box>
      </Paper>

      {/* Credentials Modal - auto-opens after creation */}
      <VendorCredentialsModal
        open={credModalOpen}
        vendorId={createdVendorId}
        vendorName={createdVendorName}
        vendorEmail={createdVendorEmail}
        onClose={handleCredentialsDone}
        onSkip={handleCredentialsDone}
      />
    </Container>
  );
};

export default VendorCreatePage;
